import apiClient, { api } from '../client';
import type {
  AccountCodeNode,
  AccountBalanceSummaryDto,
  AccountDto,
  AccountInstitutionDto,
  AccountListResponse,
  AccountResolutionDefaultDto,
  AccountStatementResponse,
  BalanceSummaryDto,
  BankDetailsDto,
  BankDetailsKindDto,
  BankDetailsRailDto,
  CreateBankDetailsRequest,
  UpdateBankDetailsRequest,
  PagedGlEntriesResult,
  PagedCryptoEntriesResult,
  PagedTransactionsResult,
  RevaluationAccountsDto,
  StaleRevaluationDayDto,
  TransactionDetailDto,
  TransactionTypeDto,
  UpdateAccountRequest,
} from '../generated/schema';

export interface AccountListParams {
  accountCode?: string;
  /** Comma-separated account codes for CoA group filter. */
  accountCodes?: string;
  currencyCode?: string;
  isActive?: boolean;
  /** Task 331: true = only accounts that accept manual journal entries. */
  allowManualEntries?: boolean;
  /** Task 514.1: true = only accounts with an active debit or credit block; false = only those without. */
  blocked?: boolean;
  customerId?: string;
  purpose?: string;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
}

export interface StatementParams {
  /**
   * Inclusive bounds on the POSTING date, `YYYY-MM-DD`.
   *
   * Task 344: these were `from` / `to`, which the endpoint does not bind — it reads `fromDate` /
   * `toDate`. The filter therefore never applied and the page returned the account's oldest entries
   * whatever range was picked (September selected, July shown).
   */
  fromDate?: string;
  toDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface GlEntriesParams {
  fromDate?: string;
  toDate?: string;
  currencyCode?: string;
  accountId?: string;
  accountCode?: string;
  accountCodes?: string;
  /** Filter entries belonging to a specific document (via GL_Transactions_Fiat.MetaDocumentId). */
  documentId?: string;
  /**
   * Task 459: false skips count + debit / credit sums in the page call (they come lazily from
   * `getGlEntriesTotals`); the three totals are then 0. Omitted = true, as before.
   */
  includeTotals?: boolean;
  pageNumber?: number;
  pageSize?: number;
  /** Task 487.4: postingDate (default) | amount. */
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
}

export interface GlTransactionsParams {
  fromDate?: string;
  toDate?: string;
  currencyCode?: string;
  accountId?: string;
  accountCode?: string;
  accountCodes?: string;
  includeEntries?: boolean;
  /** Filter transactions linked to a specific document (via MetaDocumentId). */
  documentId?: string;
  /** When true, returns only manual GL entries (Task 209-L). Pass undefined to return all. */
  isManualEntry?: boolean;
  /** Task 333: when true, returns only self-approved journals. */
  selfApproved?: boolean;
  /** Task 359: with isManualEntry, `Submitted` = journals awaiting approval. */
  status?: 'Submitted' | 'Posted' | 'Reversed' | 'Cancelled';
  /**
   * Task 459: false skips the COUNT over the whole range in the page call (it comes lazily from
   * `getGlTransactionsCount`); `totalCount` is then 0. Omitted = true, as before.
   */
  includeTotal?: boolean;
  pageNumber?: number;
  pageSize?: number;
  /** Task 487.4: transactionDate (default) | transactionNumber | amount. */
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
}

/** Task 459: the filter part of `GlTransactionsParams` — what `GET gl/transactions/count` takes. */
export type GlTransactionsCountParams = Omit<
  GlTransactionsParams,
  'includeEntries' | 'includeTotal' | 'pageNumber' | 'pageSize' | 'sortField' | 'sortOrder'
>;

/** Task 459: the filter part of `GlEntriesParams` — what `GET gl/entries/totals` takes. */
export type GlEntriesTotalsParams = Omit<
  GlEntriesParams,
  'includeTotals' | 'pageNumber' | 'pageSize' | 'sortField' | 'sortOrder'
>;

/** Task 459: `GET gl/transactions/count`. */
export interface TransactionsCountDto {
  totalCount: number;
}

/** Task 459: `GET gl/entries/totals`. */
export interface GlEntriesTotalsDto {
  totalCount: number;
  totalDebit: number;
  totalCredit: number;
}

/**
 * Task 363.1: the GL accounts the revaluation posts unrealised gains and losses to. The tenant's
 * override wins over the platform default; `scope` says which level a write targets.
 */
export async function getRevaluationAccounts(): Promise<RevaluationAccountsDto> {
  return api.get<RevaluationAccountsDto>('api/console/gl/revaluation/accounts');
}

/** Task 363.4: days whose revaluation no longer matches the ledger, newest cause first. */
export async function getStaleRevaluationDays(): Promise<StaleRevaluationDayDto[]> {
  return api.get<StaleRevaluationDayDto[]>('api/console/gl/revaluation/stale');
}

export async function setRevaluationAccounts(body: {
  scope: 'Global' | 'Tenant';
  gainAccountCode: number;
  lossAccountCode: number;
}): Promise<RevaluationAccountsDto> {
  return api.put<RevaluationAccountsDto>('api/console/gl/revaluation/accounts', { json: body });
}

export async function getAccountCodes(): Promise<AccountCodeNode[]> {
  return api.get<AccountCodeNode[]>('api/console/gl/account-codes');
}

export async function getAccounts(params: AccountListParams): Promise<AccountListResponse> {
  return api.get<AccountListResponse>('api/console/gl/accounts', {
    searchParams: params as Record<string, string | number | boolean>,
  });
}

export async function getAccountById(id: string): Promise<AccountDto> {
  return api.get<AccountDto>(`api/console/gl/accounts/${id}`);
}

export async function getAccountStatement(
  id: string,
  params: StatementParams,
): Promise<AccountStatementResponse> {
  return api.get<AccountStatementResponse>(`api/console/gl/accounts/${id}/statement`, {
    searchParams: params as Record<string, string | number>,
  });
}

export interface AccountBalanceDto {
  accountId: string;
  accountCode: number;
  accountName: string;
  currencyCode: string;
  debitBalance: number;
  creditBalance: number;
  netBalance: number;
  asOfDate?: string;
  lastUpdated: string;
  /** ADR-074 D8 (task 512.6): funds on hold as of the date; absent on an api older than 512.6. */
  held?: number | null;
  /** netBalance minus held. */
  available?: number | null;
}

export async function getAccountBalance(id: string): Promise<AccountBalanceDto> {
  return api.get<AccountBalanceDto>(`api/console/gl/accounts/${id}/balance`);
}

export interface CreateAccountParams {
  accountCode: number;
  currencyCode?: string;
  cryptoAssetCode?: string;
  customerId?: string;
  accountName?: string;
  label?: string;
  generate?: boolean;
  accountPurpose?: string;
  /**
   * Task 450.12: create a dimension sub-account the resolver never finds by code (default false). Without it a
   * second firm account under a code + currency that already has a default is refused with ACCOUNT_DEFAULT_EXISTS.
   */
  explicitOnly?: boolean;
}

/**
 * Task 526.1: mark this account as the one automatic resolution picks for its code and denomination within its
 * scope, or clear the flag. Marking moves it off the previous account in the same transaction; the answer names
 * the scope, the account it was taken from and a warning when clearing leaves resolution ambiguous.
 */
export async function setAccountResolutionDefault(
  accountId: string,
  isDefault: boolean,
): Promise<AccountResolutionDefaultDto> {
  return api.post<AccountResolutionDefaultDto>(
    `api/console/gl/accounts/${accountId}/resolution-default`,
    {
      json: { isDefault },
    },
  );
}

/**
 * Task 545.4: the institution (bank / processor) that owns one of the firm's own accounts; null moves it to the firm.
 * The api refuses a client account. Reason required; audited.
 */
export async function setAccountInstitution(
  accountId: string,
  institutionId: string | null,
  reason: string,
): Promise<AccountInstitutionDto> {
  return api.put<AccountInstitutionDto>(`api/console/gl/accounts/${accountId}/institution`, {
    json: { institutionId, reason },
  });
}

export async function createAccount(data: CreateAccountParams): Promise<AccountDto> {
  return api.post<AccountDto>('api/console/gl/accounts', { json: { generate: true, ...data } });
}

export async function updateAccount(id: string, data: UpdateAccountRequest): Promise<AccountDto> {
  return api.patch<AccountDto>(`api/console/gl/accounts/${id}`, { json: data });
}

export interface BalanceSummaryParams {
  balanceDate?: string; // YYYY-MM-DD; defaults to today on the server
  allAccounts?: boolean; // true = all accounts as of date; false = movements on date only
  accountCodePrefix?: string;
}

export async function getBalanceSummary(
  params?: BalanceSummaryParams,
): Promise<BalanceSummaryDto[]> {
  return api.get<BalanceSummaryDto[]>('api/console/gl/balances/summary', {
    searchParams: params as Record<string, string | boolean>,
  });
}

export interface BalanceAccountDetailParams {
  accountCode: number;
  currencyCode?: string;
  balanceDate?: string;
  allAccounts?: boolean;
}

export async function getBalanceAccountDetails(
  params: BalanceAccountDetailParams,
): Promise<AccountBalanceSummaryDto[]> {
  return api.get<AccountBalanceSummaryDto[]>('api/console/gl/balances/account-details', {
    searchParams: params as unknown as Record<string, string | number | boolean>,
  });
}

export async function getTransactionTypes(): Promise<TransactionTypeDto[]> {
  return api.get<TransactionTypeDto[]>('api/console/gl/transaction-types');
}

// ---------------------------------------------------------------------------
// Manual GL entry — transaction types by account (Task 209-F)
// ---------------------------------------------------------------------------

// Mirrors api TransactionTypeForAccountDto (Application/GL/TransactionTypes/DTOs). Task 450.7 (ADR-071 D4):
// the full code lists of the type's form; the first-code fields stay for callers that show one code.
export interface TransactionTypeForAccountDto {
  transactionTypeId: string;
  typeCode: string;
  typeName: string;
  description?: string | null;
  debitAccountCode?: number | null;
  creditAccountCode?: number | null;
  debitAccountName?: string | null;
  creditAccountName?: string | null;
  debitAccountCodes?: number[] | null;
  creditAccountCodes?: number[] | null;
  feeAccountCodes?: number[] | null;
  feeSide?: string | null;
}

export async function getTransactionTypesByAccount(params: {
  accountId?: string;
  accountCode?: string;
}): Promise<TransactionTypeForAccountDto[]> {
  // The console route is transaction-types/by-account; plain transaction-types lists every type unfiltered.
  return api.get<TransactionTypeForAccountDto[]>('api/console/gl/transaction-types/by-account', {
    searchParams: params as Record<string, string>,
  });
}

// ---------------------------------------------------------------------------
// Manual GL entry — hints from the type's form (Task 450.7, ADR-071 D3/D4)
// ---------------------------------------------------------------------------

export type ManualEntryLegRole = 'DEBIT' | 'CREDIT' | 'FEE';
export type ManualEntryFeeSide = 'DR' | 'CR';

// Mirrors api ManualEntryAccountCandidateDto.
export interface ManualEntryAccountCandidateDto {
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountCode: number;
  customerId?: string | null;
}

/**
 * Mirrors api ManualEntryLegHintDto. `accountId` set = the firm owns exactly one account for the leg in
 * the currency (prefill it); null with candidates = several firm accounts (operator picks one);
 * null with no candidates = a customer leg (operator picks an account carrying one of `codes`).
 */
export interface ManualEntryLegHintDto {
  role: ManualEntryLegRole;
  codes: number[];
  accountId: string | null;
  candidates: ManualEntryAccountCandidateDto[];
  note: string | null;
}

// Mirrors api ManualEntryHintsDto. `fee` null = the type has no fee leg.
export interface ManualEntryHintsDto {
  transactionTypeId: string;
  typeCode: string;
  typeName: string;
  currencyCode: string;
  debit: ManualEntryLegHintDto;
  credit: ManualEntryLegHintDto;
  fee: ManualEntryLegHintDto | null;
  feeSide: ManualEntryFeeSide | null;
}

export async function getManualEntryHints(
  transactionTypeId: string,
  currencyCode: string,
): Promise<ManualEntryHintsDto> {
  return api.get<ManualEntryHintsDto>('api/console/gl/manual-entries/hints', {
    searchParams: { transactionTypeId, currencyCode },
  });
}

// ---------------------------------------------------------------------------
// Manual GL entry — preview + create (Task 209-F)
// ---------------------------------------------------------------------------

/**
 * Mirrors api PreviewManualGlEntryDto. The api keys the type by id (TransactionTypeId), not by code.
 * Task 450.7 (ADR-071 D3): both accounts arrive resolved in either mode — in TYPE_GUIDED they come
 * from the hints or the operator's pick; the api no longer resolves them. Fee fields are optional.
 */
export interface PreviewManualGlEntryDto {
  mode: 'TYPE_GUIDED' | 'FREE_FORM';
  transactionTypeId?: string;
  debitAccountId: string;
  creditAccountId: string;
  amount: number;
  transactionDate: string;
  valueDate: string;
  currencyCode: string;
  feeAmount?: number;
  feeAccountId?: string;
  feeSide?: ManualEntryFeeSide;
}

// Mirrors api PreviewEntryLineDto (Application/GL/ManualEntry/DTOs/ManualGlEntryDtos.cs).
// Field names follow the api record; nullable members are serialised as literal null.
export interface ManualGlEntryPreviewEntryDto {
  side: 'DEBIT' | 'CREDIT';
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountCode: string;
  amount: number;
  currencyCode: string;
  estimatedBaseAmount?: number | null;
  baseCurrency?: string | null;
  estimatedRate?: number | null;
  rateDate?: string | null;
  /** Task 450.7: the leg's role in the type's form; absent on older api builds. */
  role?: ManualEntryLegRole | null;
}

// Mirrors api PeriodWarningDto. The api returns null (not an object) when the
// transaction date falls in an OPEN period — consumers must treat absence as "open".
export interface ManualGlEntryPeriodWarningDto {
  isClosed: boolean;
  canOverride: boolean;
  periodStart?: string | null;
  periodEnd?: string | null;
  closedDate?: string | null;
}

/** Task 458.5 (ADR-073 D8 as a forecast): what the approving request will do — SYNC, JOB or NONE. */
export interface ManualGlEntryExpectedRecalculationDto {
  mode: 'SYNC' | 'JOB' | 'NONE' | string;
  reason: string;
}

// Mirrors api BackdatedImpactDto. Null when the transaction date is today or later.
export interface ManualGlEntryBackdatedImpactDto {
  isBackdated: boolean;
  daysBackdated: number;
  warningMessage: string;
  affectedFiatEntriesCount: number;
  affectedBaseEntriesCount: number;
  affectedRevaluationDays: number;
  /** Task 458.5: the posting's accounts — the counts are theirs. Absent on an older api. */
  affectedAccountsCount?: number;
  /** Task 458.5: absent on an older api. */
  expectedRecalculation?: ManualGlEntryExpectedRecalculationDto | null;
}

/** Mirrors api ManualEntryRecalculationDto (task 458.3): how the posting's accounts were recalculated. */
export interface ManualGlEntryRecalculationDto {
  mode: 'SYNC' | 'JOB' | 'NONE' | string;
  jobId?: string | null;
  reason: string;
}

// Stable finding codes (api ManualEntryFindingCodes, task 331).
export type ManualEntryFindingCode =
  | 'ACCOUNT_NOT_FOUND'
  | 'ACCOUNT_INACTIVE'
  | 'ACCOUNT_MANUAL_ENTRIES_DISABLED'
  | 'ACCOUNT_CURRENCY_MISMATCH'
  | 'SAME_DEBIT_CREDIT'
  /** Task 450.7: an account or the fee leg does not fit the type's form (field names the input). */
  | 'TYPE_FORM_MISMATCH';

// Mirrors api ManualEntryFindingDto. `field` is the key the create endpoint's 400 would use.
export interface ManualEntryFindingDto {
  code: ManualEntryFindingCode | string;
  field: string;
  message: string;
}

// Mirrors api ManualEntryValidationDto — dry-run of the create-time account rules.
export interface ManualEntryValidationDto {
  canSubmit: boolean;
  findings: ManualEntryFindingDto[];
}

// Mirrors api ManualGlEntryPreviewDto (task 330 aligned the shape with the api record;
// task 331 added requestCurrencyCode + validation, and lines now carry the ACCOUNT currency).
export interface ManualGlEntryPreviewDto {
  mode: 'TYPE_GUIDED' | 'FREE_FORM';
  transactionTypeName?: string | null;
  entries: ManualGlEntryPreviewEntryDto[];
  periodWarning?: ManualGlEntryPeriodWarningDto | null;
  backdatedImpact?: ManualGlEntryBackdatedImpactDto | null;
  /** Currency the operator entered the amount in. Optional until the api build with 331 is deployed. */
  requestCurrencyCode?: string;
  /** Optional until the api build with 331 is deployed; absence = no dry-run available. */
  validation?: ManualEntryValidationDto | null;
}

/** Task 450.7: the optional fee leg and per-side narratives (each narrative max 200 chars). */
export interface ManualGlEntryFeeAndNarratives {
  feeAmount?: number;
  feeAccountId?: string;
  feeSide?: ManualEntryFeeSide;
  debitNarrative?: string;
  creditNarrative?: string;
  feeNarrative?: string;
}

export interface CreateManualGlEntryDto extends ManualGlEntryFeeAndNarratives {
  mode: 'TYPE_GUIDED' | 'FREE_FORM';
  transactionTypeId?: string;
  debitAccountId: string;
  creditAccountId: string;
  amount: number;
  transactionDate: string;
  valueDate: string;
  currencyCode: string;
  description: string;
  reason: string;
  periodOverrideReason?: string;
  backdatedConfirmed?: boolean;
  /** Task 451.5a: the quick-entry suggestion the entry was opened from; the api links them. */
  suggestionId?: string;
}

// Mirrors api ManualGlEntryResultDto (create, submit and approve).
export interface ManualGlEntryResultDto {
  transactionId: string;
  transactionNumber: string;
  status: string;
  transactionDate?: string;
  /** Task 458.5: the same shape as the preview's backdatedImpact (the posting's accounts). */
  backdatedWarning?: ManualGlEntryBackdatedImpactDto | null;
  /** Task 458.3: set by the approving request when the entry was back-dated; null otherwise. */
  recalculation?: ManualGlEntryRecalculationDto | null;
}

export async function previewManualGlEntry(
  dto: PreviewManualGlEntryDto,
): Promise<ManualGlEntryPreviewDto> {
  return api.post<ManualGlEntryPreviewDto>('api/console/gl/manual-entries/preview', { json: dto });
}

export async function createManualGlEntry(
  dto: CreateManualGlEntryDto,
): Promise<ManualGlEntryResultDto> {
  return api.post<ManualGlEntryResultDto>('api/console/gl/manual-entries', { json: dto });
}

// ---------------------------------------------------------------------------
// Quick manual entry — suggestions from a note (task 451.2 api, 451.3 web)
// ---------------------------------------------------------------------------

/** RESOLVED: one account fits; CHOOSE: the operator picks one of the options; MISSING: picked in the form. */
export type SuggestionLegStatus = 'RESOLVED' | 'CHOOSE' | 'MISSING';

export interface SuggestionAccountOptionDto {
  accountId: string;
  accountCode: number;
  accountNumber: string;
  accountName: string;
  currencyCode: string | null;
  customerName: string | null;
}

export interface SuggestionLegDto {
  status: SuggestionLegStatus;
  accountCodes: number[];
  options: SuggestionAccountOptionDto[];
  /** The options come from a name in the note, not from the leg's only account. */
  named: boolean;
  /** The filled account when RESOLVED, else null. */
  account: SuggestionAccountOptionDto | null;
}

export interface SuggestionCandidateDto {
  transactionTypeId: string;
  typeCode: string;
  typeName: string;
  explanation: string;
  confidence: number;
  debit: SuggestionLegDto;
  credit: SuggestionLegDto;
  /** The currency the legs were matched in; null when the operator picks it. */
  currencyCode: string | null;
  /** The note named no currency: the tenant's base currency is recommended. */
  currencyAssumed: boolean;
  /** The manual entry preview's checks; null while a leg or the amount is open. */
  validation: ManualEntryValidationDto | null;
}

export interface SuggestionWarningDto {
  /** NO_MATCH | AMOUNT_MISSING | CURRENCY_MISSING | CURRENCY_ASSUMED | BACKDATED | PERIOD | IGNORED */
  code: string;
  message: string;
}

export interface ManualEntrySuggestionDto {
  suggestionId: string;
  amount: number | null;
  currencyCode: string | null;
  /** yyyy-MM-dd */
  transactionDate: string;
  /** yyyy-MM when the note named a month. */
  period: string | null;
  description: string;
  warnings: SuggestionWarningDto[];
  candidates: SuggestionCandidateDto[];
}

/** 503 when the model is unavailable: the operator fills the form by hand. */
export async function suggestManualGlEntry(text: string): Promise<ManualEntrySuggestionDto> {
  return api.post<ManualEntrySuggestionDto>('api/console/gl/manual-entries/suggest', {
    json: { text },
  });
}

/** The operator opened a candidate in the form (audit for the eval set and the learning loop). */
export async function acceptManualEntrySuggestion(
  suggestionId: string,
  typeCode: string,
): Promise<void> {
  // 204 No Content: no body to parse.
  await apiClient.post(`api/console/gl/manual-entries/suggestions/${suggestionId}/accept`, {
    json: { typeCode },
  });
}

export async function getGlEntries(params: GlEntriesParams): Promise<PagedGlEntriesResult> {
  return api.get<PagedGlEntriesResult>('api/console/gl/entries', {
    searchParams: params as Record<string, string | number | boolean>,
  });
}

export async function getGlTransactions(
  params: GlTransactionsParams,
): Promise<PagedTransactionsResult> {
  return api.get<PagedTransactionsResult>('api/console/gl/transactions', {
    searchParams: params as Record<string, string | number | boolean>,
  });
}

/** Task 486: one transaction for the transaction page — legs, accounts and codes named, who raised and posted it. */
export async function getGlTransaction(id: string): Promise<TransactionDetailDto> {
  return api.get<TransactionDetailDto>(`api/console/gl/transactions/${id}`);
}

/**
 * Task 459: the ledger's row count for a filter set, asked for after the page rendered. The api
 * caches it per tenant + filters for 20 s, so paging and a refresh inside that window do not recount.
 */
export async function getGlTransactionsCount(
  params: GlTransactionsCountParams,
): Promise<TransactionsCountDto> {
  return api.get<TransactionsCountDto>('api/console/gl/transactions/count', {
    searchParams: params as Record<string, string | number | boolean>,
  });
}

/** Task 459: the journal view's count and debit / credit sums, same caching as the transactions count. */
export async function getGlEntriesTotals(
  params: GlEntriesTotalsParams,
): Promise<GlEntriesTotalsDto> {
  return api.get<GlEntriesTotalsDto>('api/console/gl/entries/totals', {
    searchParams: params as Record<string, string | number | boolean>,
  });
}

export async function getDocumentCryptoEntries(
  documentId: string,
  pageNumber = 1,
  pageSize = 100,
): Promise<PagedCryptoEntriesResult> {
  return api.get<PagedCryptoEntriesResult>('api/console/gl/crypto-entries', {
    searchParams: { documentId, pageNumber, pageSize },
  });
}

// ---------------------------------------------------------------------------
// Balance recalculation (Task 185)
// ---------------------------------------------------------------------------

export interface RecalcPreflightDto {
  fromDate: string;
  toDate: string;
  affectedAccounts: number;
  dateRangeSpan: number;
  estimatedRows: number;
  existingBalanceRowsToOverwrite: number;
  accountsWithBackdatedEntries: number;
  currenciesWithMissingRates: string[];
  lastRecalcFromDate?: string;
  lastRecalcToDate?: string;
  lastRecalcStatus?: string;
  lastRecalcStartedAt?: string;
}

export interface RecalculateBalancesResultDto {
  jobId: string;
  fromDate: string;
  toDate: string;
  accountsProcessed: number;
  daysProcessed: number;
  rowsUpserted: number;
  failedRows: number;
  status: string;
  startedAt: string;
  completedAt?: string;
  errorSummary?: string;
}

export interface RecalcJobStatusDto {
  jobId: string;
  jobType: string;
  /** Task 458.2: per-day scope of an ENTRIES_BASE_REBUILD job (FULL_DAY / REVALUATION / REVALUATION_ROWS). */
  mode?: string;
  status: string;
  fromDate: string;
  toDate: string;
  totalDays: number;
  totalAccounts: number;
  processedDays: number;
  processedAccounts: number;
  failedRows: number;
  rowsUpserted: number;
  progressPercent: number;
  isSurgical: boolean;
  errorSummary?: string;
  startedAt: string;
  completedAt?: string;
}

/** Task 458.2 (ADR-073): what a rebuild endpoint returns with 202 Accepted — the job to poll. */
export interface RecalcJobAcceptedDto {
  jobId: string;
  jobType: string;
  mode?: string;
  status: string;
  fromDate: string;
  toDate: string;
  totalDays: number;
  startedAt: string;
  /** True when an active job already covered the range and no new job was created. */
  alreadyQueued: boolean;
}

/** Task 458.2: one Pending / Running recalculation job of the tenant (GET api/console/gl/jobs/active). */
export interface ActiveRecalcJobDto {
  jobId: string;
  jobType: string;
  mode?: string;
  status: string;
  fromDate: string;
  toDate: string;
  processedDays: number;
  totalDays: number;
  processedAccounts: number;
  totalAccounts: number;
  startedAt: string;
  heartbeatAt?: string;
}

/** A job still owned by the runner: Pending (queued) or Running. Anything else is terminal. */
export function isRecalcJobActive(status: string | undefined): boolean {
  return status === 'Pending' || status === 'Running';
}

export interface GlHealthSummaryDto {
  accountsRequiringRevaluation: number;
  accountsWithBackdatedTransactions: number;
  daysWithMissingRevaluation: number;
  latestRecalcFromDate?: string;
  latestRecalcToDate?: string;
  latestRecalcStatus?: string;
  latestRecalcAt?: string;
  latestRevaluationDate?: string;
  latestRevaluationStatus?: string;
  latestRevaluationAt?: string;
  // Task 208: actual-date tracking
  balanceActualDate?: string;
  revaluationActualDate?: string;
}

// Task 208: actual dates
export interface GlActualDatesDto {
  balanceActualDate?: string;
  revaluationActualDate?: string;
}

// Task 208: job history
export interface RecalcJobHistoryDto {
  jobId: string;
  jobType: string;
  status: string;
  fromDate: string;
  toDate: string;
  totalAccounts: number;
  processedAccounts: number;
  totalDays: number;
  processedDays: number;
  rowsUpserted: number;
  failedRows: number;
  isSurgical: boolean;
  errorSummary?: string;
  startedAt: string;
  completedAt?: string;
  durationSeconds?: number;
  /** Task 458.5: what an entries-base job rebuilt per day; absent for other job types or an older api. */
  mode?: string | null;
}

export interface PagedRecalcJobHistoryDto {
  items: RecalcJobHistoryDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface BackdatedAccountDto {
  accountId: string;
  accountNumber: string;
  accountCode: number;
  accountName: string;
  currencyCode: string;
  latestBalanceDate?: string;
  earliestBackdatedEntryAt: string;
  backdatedEntryCount: number;
}

export async function getRecalcPreflight(
  fromDate: string,
  toDate?: string,
): Promise<RecalcPreflightDto> {
  return api.get<RecalcPreflightDto>('api/console/gl/balances/recalculate/preflight', {
    searchParams: { fromDate, ...(toDate ? { toDate } : {}) },
  });
}

export async function triggerRecalculateBalances(
  fromDate: string,
  toDate?: string,
  accountIds?: string[],
): Promise<RecalculateBalancesResultDto> {
  return api.post<RecalculateBalancesResultDto>('api/console/gl/balances/recalculate', {
    json: { fromDate, toDate, accountIds },
  });
}

export async function triggerRecalculateSurgical(): Promise<RecalculateBalancesResultDto> {
  return api.post<RecalculateBalancesResultDto>('api/console/gl/balances/recalculate/surgical', {});
}

export async function getRecalcJobStatus(
  jobId?: string,
  jobType?: string,
): Promise<RecalcJobStatusDto | null> {
  return api.get<RecalcJobStatusDto>('api/console/gl/balances/recalculate/status', {
    searchParams: {
      ...(jobId ? { jobId } : {}),
      ...(jobType ? { jobType } : {}),
    },
  });
}

export async function triggerRevaluationRange(
  fromDate: string,
  toDate: string,
): Promise<RecalculateBalancesResultDto> {
  return api.post<RecalculateBalancesResultDto>('api/console/gl/revaluation/calculate-range', {
    json: { fromDate, toDate },
  });
}

/** Task 458.2: active recalculation jobs of every type, oldest first — drives the shell badge (ADR-073 D12). */
export async function getActiveRecalcJobs(): Promise<ActiveRecalcJobDto[]> {
  return api.get<ActiveRecalcJobDto[]>('api/console/gl/jobs/active');
}

export async function getGlHealthSummary(): Promise<GlHealthSummaryDto> {
  return api.get<GlHealthSummaryDto>('api/console/gl/health');
}

export async function getBackdatedAccounts(): Promise<BackdatedAccountDto[]> {
  return api.get<BackdatedAccountDto[]>('api/console/gl/health/backdated-accounts');
}

// Task 208: actual dates + history

export async function getGlActualDates(): Promise<GlActualDatesDto> {
  return api.get<GlActualDatesDto>('api/console/gl/actual-dates');
}

export async function getRecalcJobsHistory(
  jobType: string,
  page: number,
  pageSize: number,
): Promise<PagedRecalcJobHistoryDto> {
  return api.get<PagedRecalcJobHistoryDto>('api/console/gl/jobs/history', {
    searchParams: { jobType, page: String(page), pageSize: String(pageSize) },
  });
}

// ---------------------------------------------------------------------------
// Accounting periods (Task 209-N)
// ---------------------------------------------------------------------------

export interface GlPeriodDto {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: 'CLOSED' | 'OPEN';
  closedBy?: string;
  closedAt?: string;
  reopenedBy?: string;
  reopenedAt?: string;
  notes?: string;
}

export interface PagedGlPeriodsResult {
  items: GlPeriodDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface CloseGlPeriodDto {
  periodStart: string;
  periodEnd: string;
  notes?: string;
}

export interface ReopenGlPeriodDto {
  reason: string;
}

export interface PeriodCheckDto {
  date: string;
  isClosed: boolean;
  canOverride: boolean;
  periodId?: string;
  periodStart?: string;
  periodEnd?: string;
}

export async function getGlPeriods(params?: { status?: string }): Promise<PagedGlPeriodsResult> {
  return api.get<PagedGlPeriodsResult>('api/console/gl/periods', {
    searchParams: params as Record<string, string>,
  });
}

export async function closeGlPeriod(dto: CloseGlPeriodDto): Promise<GlPeriodDto> {
  return api.post<GlPeriodDto>('api/console/gl/periods/close', { json: dto });
}

export async function reopenGlPeriod(id: string, dto: ReopenGlPeriodDto): Promise<GlPeriodDto> {
  return api.post<GlPeriodDto>(`api/console/gl/periods/${id}/reopen`, { json: dto });
}

export async function checkGlPeriod(date: string): Promise<PeriodCheckDto> {
  return api.get<PeriodCheckDto>('api/console/gl/periods/check', {
    searchParams: { date },
  });
}

// ---------------------------------------------------------------------------
// Transaction reversal (Task 209-G)
// ---------------------------------------------------------------------------

export interface ReverseTransactionDto {
  reason: string;
  periodOverrideReason?: string;
}

export interface ReverseTransactionResultDto {
  reversalTransactionId: string;
  reversalTransactionNumber: string;
  backdatedWarning?: {
    isBackdated: boolean;
    affectedDateRangeStart?: string;
    affectedDateRangeEnd?: string;
  };
}

export async function reverseGlTransaction(
  id: string,
  dto: ReverseTransactionDto,
): Promise<ReverseTransactionResultDto> {
  return api.post<ReverseTransactionResultDto>(`api/console/gl/transactions/${id}/reverse`, {
    json: dto,
  });
}

// ---------------------------------------------------------------------------
// Draft manual GL entry — edit / delete (Task 209)
// ---------------------------------------------------------------------------

export interface DraftManualGlEntryDto {
  transactionId: string;
  transactionNumber: string;
  status: string;
  mode: 'TYPE_GUIDED' | 'FREE_FORM';
  transactionTypeId?: string;
  transactionTypeCode?: string;
  transactionTypeName?: string;
  debitAccountId: string;
  debitAccountNumber: string;
  debitAccountName: string;
  currencyCode: string;
  creditAccountId: string;
  creditAccountNumber: string;
  creditAccountName: string;
  amount: number;
  transactionDate: string;
  valueDate: string;
  description?: string;
  reason?: string;
  // Task 450.7: fee leg + per-side narratives.
  feeAmount?: number | null;
  feeAccountId?: string | null;
  feeAccountNumber?: string | null;
  feeAccountName?: string | null;
  feeSide?: ManualEntryFeeSide | null;
  debitNarrative?: string | null;
  creditNarrative?: string | null;
  feeNarrative?: string | null;
}

export interface UpdateDraftManualGlEntryDto extends ManualGlEntryFeeAndNarratives {
  transactionTypeId?: string;
  debitAccountId?: string;
  creditAccountId?: string;
  transactionDate: string;
  valueDate: string;
  amount: number;
  currencyCode: string;
  description: string;
  reason: string;
  periodOverrideReason?: string;
}

export async function getDraftManualEntry(id: string): Promise<DraftManualGlEntryDto> {
  return api.get<DraftManualGlEntryDto>(`api/console/gl/manual-entries/${id}`);
}

export async function updateDraftManualEntry(
  id: string,
  dto: UpdateDraftManualGlEntryDto,
): Promise<ManualGlEntryResultDto> {
  // PUT, not PATCH — the console route is [HttpPut("{id:guid}")] (a PATCH here 405s).
  return api.put<ManualGlEntryResultDto>(`api/console/gl/manual-entries/${id}`, { json: dto });
}

export async function deleteDraftManualEntry(id: string): Promise<void> {
  return api.del(`api/console/gl/manual-entries/${id}`);
}

// Manual-journal maker-checker (task 326.9): a pending entry is posted or rejected by a
// second operator holding GL_MANUAL_ENTRY_APPROVE.

export interface ApproveManualGlEntryDto {
  approvalNote?: string;
  periodOverrideReason?: string;
  /** Task 333: required when approving your own entry (tenant self-approval waiver). */
  selfApprovalReason?: string;
}

export async function approveManualGlEntry(
  id: string,
  dto: ApproveManualGlEntryDto = {},
): Promise<ManualGlEntryResultDto> {
  return api.post<ManualGlEntryResultDto>(`api/console/gl/manual-entries/${id}/approve`, {
    json: dto,
  });
}

export async function rejectManualGlEntry(id: string, reason: string): Promise<void> {
  return api.post<void>(`api/console/gl/manual-entries/${id}/reject`, { json: { reason } });
}

// ---------------------------------------------------------------------------
// Bank Details (Task 214)
// ---------------------------------------------------------------------------

export async function getBankDetails(accountId: string): Promise<BankDetailsDto[]> {
  return api.get<BankDetailsDto[]>(`api/console/gl/accounts/${accountId}/bankdetails`);
}

/** The bank-details kinds the add form offers, tenant overrides applied (task 422.12b). */
export async function getBankDetailsKinds(): Promise<BankDetailsKindDto[]> {
  return api.get<BankDetailsKindDto[]>('api/console/gl/bankdetails-kinds');
}

/** Task 517.9: the rails a bank-details row can carry, with the currencies the tenant's routes send on each. */
export async function getBankDetailsRails(): Promise<BankDetailsRailDto[]> {
  return api.get<BankDetailsRailDto[]>('api/console/gl/bankdetails-rails');
}

export async function addBankDetails(
  accountId: string,
  data: CreateBankDetailsRequest,
): Promise<BankDetailsDto> {
  return api.post<BankDetailsDto>(`api/console/gl/accounts/${accountId}/bankdetails`, {
    json: data,
  });
}

export async function updateBankDetails(
  accountId: string,
  bankDetailsId: string,
  data: UpdateBankDetailsRequest,
): Promise<BankDetailsDto> {
  return api.patch<BankDetailsDto>(
    `api/console/gl/accounts/${accountId}/bankdetails/${bankDetailsId}`,
    { json: data },
  );
}

export async function setBankDetailsDefault(
  accountId: string,
  bankDetailsId: string,
): Promise<void> {
  return api.post<void>(
    `api/console/gl/accounts/${accountId}/bankdetails/${bankDetailsId}/set-default`,
  );
}

export async function setBankDetailsActive(
  accountId: string,
  bankDetailsId: string,
  isActive: boolean,
): Promise<BankDetailsDto> {
  return api.patch<BankDetailsDto>(
    `api/console/gl/accounts/${accountId}/bankdetails/${bankDetailsId}/active`,
    { json: { isActive } },
  );
}

export async function deleteBankDetails(
  accountId: string,
  bankDetailsId: string,
): Promise<BankDetailsDto[]> {
  return apiClient
    .delete(`api/console/gl/accounts/${accountId}/bankdetails/${bankDetailsId}`)
    .json<BankDetailsDto[]>();
}
